import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TalenteesLogo from '../../image/Talentees-02.png';
import TalenteesLogoSmall from '../../image/Talentees-04.png';
import { useI18n, Language } from '../../i18n';

interface NavMorphProps {
  onGetInTouchClick?: () => void;
}

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'FR', label: 'Français' },
  { code: 'EN', label: 'English' },
  { code: 'DE', label: 'Deutsch' },
];

// Hamburger icon component
const HamburgerIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <div className="w-5 h-4 relative flex flex-col justify-between">
    <span
      className={`w-full h-0.5 bg-white/80 rounded-full transition-all duration-300 ${
        isOpen ? 'rotate-45 translate-y-[7px]' : ''
      }`}
    />
    <span
      className={`w-full h-0.5 bg-white/80 rounded-full transition-all duration-300 ${
        isOpen ? 'opacity-0' : ''
      }`}
    />
    <span
      className={`w-full h-0.5 bg-white/80 rounded-full transition-all duration-300 ${
        isOpen ? '-rotate-45 -translate-y-[7px]' : ''
      }`}
    />
  </div>
);

export const NavMorph: React.FC<NavMorphProps> = ({ onGetInTouchClick }) => {
  const { lang, setLang, t } = useI18n();
  const [isFloating, setIsFloating] = useState(false);
  const [isTopNavVisible, setIsTopNavVisible] = useState(true);
  const [isLangOpenHero, setIsLangOpenHero] = useState(false);
  const [isLangOpenFloating, setIsLangOpenFloating] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const langHeroRef = useRef<HTMLDivElement>(null);
  const langFloatingRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Scroll-based nav transition: top nav hides and floating nav appears
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Hide top nav after scrolling 50px
      setIsTopNavVisible(scrollY < 50);
      // Show floating nav after scrolling 400px
      setIsFloating(scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close lang dropdown when clicking outside - Hero
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langHeroRef.current && !langHeroRef.current.contains(event.target as Node)) {
        setIsLangOpenHero(false);
      }
    };

    if (isLangOpenHero) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangOpenHero]);

  // Close lang dropdown when clicking outside - Floating
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langFloatingRef.current && !langFloatingRef.current.contains(event.target as Node)) {
        setIsLangOpenFloating(false);
      }
    };

    if (isLangOpenFloating) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangOpenFloating]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  const handleLangChange = (code: Language) => {
    setLang(code);
    setIsLangOpenHero(false);
    setIsLangOpenFloating(false);
    setIsMobileMenuOpen(false);
  };

  const navItems: { label: string; href: string }[] = [];

  const handleGetInTouchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (onGetInTouchClick) {
      onGetInTouchClick();
    }
  };

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Top Nav State - Visible at page top, hides on scroll */}
      <AnimatePresence>
        {isTopNavVisible && !isFloating && (
          <motion.nav
            key="top-nav"
            className="fixed top-0 left-0 right-0 z-[9999] px-8 py-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <div className="max-w-[1600px] mx-auto flex items-center justify-between">
              {/* Logo */}
              <a href="/" className="flex items-center hover:opacity-70 transition-opacity">
                <img src={TalenteesLogo} alt="Talentees" className="h-7 opacity-90" />
              </a>

              {/* Nav Links - Desktop */}
              <div className="hidden md:flex items-center gap-6">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm text-white/60 hover:text-white/90 transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Right side buttons - Desktop only */}
              <div className="hidden md:flex items-center gap-3">
                {/* Language Selector */}
                <div className="relative" ref={langHeroRef}>
                  <button
                    onClick={() => setIsLangOpenHero(!isLangOpenHero)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-sm text-white/70 hover:text-white transition-all duration-200"
                  >
                    <span>{lang}</span>
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${isLangOpenHero ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isLangOpenHero && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full right-0 mt-2 w-36 rounded-xl border border-white/10 overflow-hidden"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.85)',
                          backdropFilter: 'blur(12px)',
                          boxShadow: '0 18px 70px rgba(0, 0, 0, 0.6)',
                        }}
                      >
                        <div className="p-1">
                          {LANGUAGES.map((l) => (
                            <button
                              key={l.code}
                              onClick={() => handleLangChange(l.code)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors duration-200 ${
                                lang === l.code
                                  ? 'bg-white/10 text-white'
                                  : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                              }`}
                            >
                              <span className="font-medium">{l.code}</span>
                              <span className="text-white/50">{l.label}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Se connecter */}
                <a
                  href="https://app.talentees.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2 rounded-full text-sm text-white/70 hover:text-white font-medium transition-all duration-200"
                >
                  {t.nav.login}
                </a>

                {/* Entrer en contact */}
                <button
                  onClick={handleGetInTouchClick}
                  className="px-6 py-2.5 rounded-full border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                >
                  {t.nav.contact}
                </button>
              </div>

              {/* Mobile hamburger button */}
              <button
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Menu"
              >
                <HamburgerIcon isOpen={isMobileMenuOpen} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-0 right-0 h-full w-[85%] max-w-sm bg-black/95 backdrop-blur-xl border-l border-white/10 z-[9999] md:hidden overflow-y-auto"
            >
              {/* Close button */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <img src={TalenteesLogo} alt="Talentees" className="h-6 opacity-90" />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200"
                  aria-label="Close menu"
                >
                  <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu content */}
              <div className="p-6">
                {/* Nav items */}
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={handleMobileNavClick}
                    className="block py-3 text-lg font-medium text-white/90 hover:text-white transition-colors"
                  >
                    {item.label}
                  </a>
                ))}

                {/* Divider */}
                <div className="my-6 border-t border-white/10" />

                {/* Language selector */}
                <div className="mb-6">
                  <p className="text-sm text-white/50 mb-3">Language</p>
                  <div className="flex gap-2">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => handleLangChange(l.code)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          lang === l.code
                            ? 'bg-white/10 text-white border border-white/20'
                            : 'bg-white/[0.03] text-white/60 hover:bg-white/[0.06] hover:text-white border border-transparent'
                        }`}
                      >
                        {l.code}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="space-y-3">
                  <a
                    href="https://app.talentees.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleMobileNavClick}
                    className="block w-full py-3 text-center rounded-xl border border-white/20 bg-white/5 text-white font-medium hover:bg-white/10 transition-all duration-200"
                  >
                    {t.nav.login}
                  </a>
                  <button
                    onClick={handleGetInTouchClick}
                    className="w-full py-3 rounded-xl bg-white text-black font-medium hover:bg-white/90 transition-all duration-200"
                  >
                    {t.nav.contact}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Floating Nav State - Clean pill, drop-in effect */}
      <AnimatePresence>
        {isFloating && (
          <motion.nav
            key="floating-nav"
            className="fixed top-4 md:top-6 left-1/2 z-[9999] w-[calc(100%-2rem)] md:w-auto max-w-[calc(100%-2rem)]"
            initial={{ x: '-50%', y: -18, opacity: 0, scale: 0.98 }}
            animate={{ x: '-50%', y: 0, opacity: 1, scale: 1 }}
            exit={{ x: '-50%', y: -18, opacity: 0, scale: 0.98 }}
            transition={{
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <div
              className="flex items-center justify-between md:justify-start gap-3 md:gap-6 px-4 md:px-5 py-2.5 md:py-3 rounded-full border"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
                borderColor: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: '0 18px 70px rgba(0, 0, 0, 0.55)',
              }}
            >
              {/* Logo - Use compact logo for floating nav */}
              <a href="/" className="flex items-center hover:opacity-70 transition-opacity">
                <img src={TalenteesLogoSmall} alt="Talentees" className="h-6" />
              </a>

              {/* Nav Links - Desktop */}
              <div className="hidden md:flex items-center gap-5">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm text-white/70 hover:text-white transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Desktop buttons - Hidden on mobile */}
              <div className="hidden md:flex items-center gap-4">
                {/* Language Selector - Floating */}
                <div className="relative" ref={langFloatingRef}>
                  <button
                    onClick={() => setIsLangOpenFloating(!isLangOpenFloating)}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-xs text-white/70 hover:text-white transition-all duration-200"
                  >
                    <span>{lang}</span>
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${isLangOpenFloating ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isLangOpenFloating && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full right-0 mt-2 w-36 rounded-xl border border-white/10 overflow-hidden"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.85)',
                          backdropFilter: 'blur(12px)',
                          boxShadow: '0 18px 70px rgba(0, 0, 0, 0.6)',
                        }}
                      >
                        <div className="p-1">
                          {LANGUAGES.map((l) => (
                            <button
                              key={l.code}
                              onClick={() => handleLangChange(l.code)}
                              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors duration-200 ${
                                lang === l.code
                                  ? 'bg-white/10 text-white'
                                  : 'text-white/70 hover:bg-white/[0.06] hover:text-white'
                              }`}
                            >
                              <span className="font-medium">{l.code}</span>
                              <span className="text-white/50">{l.label}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Se connecter - Floating */}
                <a
                  href="https://app.talentees.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-1.5 rounded-full text-xs text-white/70 hover:text-white font-medium transition-all duration-200"
                >
                  {t.nav.login}
                </a>

                {/* CTA - Cleaner style */}
                <button
                  onClick={handleGetInTouchClick}
                  className="px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-[1.01]"
                  style={{
                    backgroundColor: '#F3F4F6',
                    color: '#111827',
                  }}
                >
                  {t.nav.contact}
                </button>
              </div>

              {/* Mobile hamburger button - Floating nav */}
              <button
                className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Menu"
              >
                <HamburgerIcon isOpen={isMobileMenuOpen} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};
